import { Form, Formik, useFormikContext } from "formik";
import { assetTypeEnum } from "kubb";
import { isEqual } from "lodash-es";
import type React from "react";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ButtonGroupField } from "../../../../components/Forms/ButtonGroup";
import { SelectComboField } from "../../../../components/Forms/SelectCombo/SelectCombo";
import { Button } from "../../../../components/Theme/button";
import { Col, Container } from "../../../../components/Theme/grid";
import {
  convertStringUndefined,
  createSearchString,
} from "../../../../components/navigation/SearchString";
import Spinner from "../../../../core/action/Spinner";
import {
  filterOptionsToSelectOptions,
  useAssetFilterOptions,
} from "../../../../hooks/useAssetFilterOptions";

export interface GatewayFilterParams {
  manufacturer?: string[];
  model?: string[];
  firmware_version?: string[];
  configuration_state?: string[];
  connectivity?: string[];
}

interface Props {
  company_id: string;
  scheme_id?: string;
  params: GatewayFilterParams;
  onSubmit?: (values: GatewayFilterParams) => void;
}

const orUndefined = (arr: string[] | undefined): string[] | undefined =>
  arr?.length ? arr : undefined;

export default function GatewayFilterForm(props: Props): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id, scheme_id, params } = props;

  const setFiltersToRoute = useCallback(
    (values: GatewayFilterParams): void => {
      const newSearchString = createSearchString(
        convertStringUndefined({ ...values }),
      );

      if (
        location.search === ""
          ? newSearchString !== ""
          : `?${newSearchString}` !== location.search
      ) {
        navigate({
          ...location,
          search: newSearchString,
        });
      }
    },
    [navigate, location],
  );

  const currentValues: GatewayFilterParams = {
    manufacturer: params.manufacturer || [],
    model: params.model || [],
    firmware_version: params.firmware_version || [],
    configuration_state: params.configuration_state || [],
    connectivity: params.connectivity || [],
  };

  return (
    <Formik
      initialValues={currentValues}
      enableReinitialize
      onSubmit={(values: GatewayFilterParams): void => {
        if (props.onSubmit) {
          props.onSubmit(values);
        } else {
          setFiltersToRoute(values);
        }
      }}
    >
      <GatewayFilterFormFields
        company_id={company_id}
        scheme_id={scheme_id}
        currentValues={currentValues}
      />
    </Formik>
  );
}

function GatewayFilterFormFields({
  company_id,
  scheme_id,
  currentValues,
}: {
  company_id: string;
  scheme_id?: string;
  currentValues: GatewayFilterParams;
}): React.ReactElement {
  const { values } = useFormikContext<GatewayFilterParams>();

  // Fetch filter options using current form values (updates as user changes selections)
  const {
    data: filterOptions,
    isLoading,
    isFetching,
    isError,
  } = useAssetFilterOptions({
    company_id,
    scheme_id: scheme_id || undefined,
    asset_type: [assetTypeEnum.gateway],
    asset_position_id: "NOT_NONE",
    manufacturer: orUndefined(values.manufacturer),
    model: orUndefined(values.model),
    firmware_version: orUndefined(values.firmware_version),
    configuration_state: orUndefined(values.configuration_state),
    connectivity: orUndefined(values.connectivity),
  });

  // Convert filter options to SelectCombo options
  const manufacturerOptions = useMemo(
    () => filterOptionsToSelectOptions(filterOptions?.manufacturer),
    [filterOptions?.manufacturer],
  );

  const modelOptions = useMemo(
    () => filterOptionsToSelectOptions(filterOptions?.model),
    [filterOptions?.model],
  );

  const firmwareOptions = useMemo(
    () => filterOptionsToSelectOptions(filterOptions?.firmware_version),
    [filterOptions?.firmware_version],
  );

  const configStateOptions = useMemo(
    () => filterOptionsToSelectOptions(filterOptions?.configuration_state),
    [filterOptions?.configuration_state],
  );

  return (
    <Form>
      {isError && (
        <div className="text-red-600 text-sm mb-2">
          Failed to load filter options. Please try again.
        </div>
      )}
      <Container gap={2}>
        <Col sm={12} md={3}>
          <SelectComboField
            fieldName="manufacturer"
            label={{ label: "Manufacturer", size: "sm" }}
            options={manufacturerOptions}
            isMulti={true}
            placeholder={
              isLoading ? "Loading..." : isError ? "Error" : "All manufacturers"
            }
            disabled={isLoading}
          />
        </Col>
        <Col sm={12} md={3}>
          <SelectComboField
            fieldName="model"
            label={{ label: "Model", size: "sm" }}
            options={modelOptions}
            isMulti={true}
            placeholder={
              isLoading ? "Loading..." : isError ? "Error" : "All models"
            }
            disabled={isLoading}
          />
        </Col>
        <Col sm={12} md={3}>
          <SelectComboField
            fieldName="firmware_version"
            label={{ label: "Firmware", size: "sm" }}
            options={firmwareOptions}
            isMulti={true}
            placeholder={
              isLoading ? "Loading..." : isError ? "Error" : "All versions"
            }
            disabled={isLoading}
          />
        </Col>
        <Col sm={12} md={3}>
          <SelectComboField
            fieldName="configuration_state"
            label={{ label: "Config State", size: "sm" }}
            options={configStateOptions}
            isMulti={true}
            placeholder={
              isLoading ? "Loading..." : isError ? "Error" : "All states"
            }
            disabled={isLoading || !filterOptions?.configuration_state}
          />
        </Col>
        <Col sm={12} md={3}>
          <ButtonGroupField
            fieldName="connectivity"
            label={{ label: "Connectivity", size: "sm" }}
            options={[
              { label: "All", value: [] },
              { label: "Online", value: ["ONLINE"] },
              { label: "Offline", value: ["OFFLINE"] },
            ]}
            fullWidth={true}
          />
        </Col>
        <Col sm={12} md={2} className="flex items-end gap-2">
          <Button
            type="submit"
            className="w-full h-[38px]"
            disabled={isEqual(values, currentValues)}
          >
            Filter
          </Button>
          <Spinner
            spin={isFetching && !isLoading}
            className="text-zinc-400 mb-2"
          />
        </Col>
      </Container>
    </Form>
  );
}
